import React from 'react';
import PropTypes from 'prop-types';
import { cx, css } from 'emotion';
import ThemeContext from '@weave-design/theme-context';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const sequences = Object.freeze({
  ABC: "abc",
  ACB: "acb",
  BAC: "bac",
  BCA: "bca",
  CAB: "cab",
  CBA: "cba"
});
const availableSequences = Object.freeze(Object.values(sequences));

function stylesheet(props, themeData) {
  const {
    stylesheet: customStylesheet
  } = props;
  const styles = {
    timestamp: {
      color: themeData["timestamp.fontColor"],
      fontFamily: themeData["timestamp.fontFamily"],
      fontSize: themeData["timestamp.fontSize"],
      fontWeight: themeData["timestamp.fontWeight"],
      lineHeight: themeData["timestamp.lineHeight"],
      margin: 0
    }
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

const _excluded = ["stylesheet"];

const pluralize = (word, count) => count === 1 ? word : `${word}s`;

const Timestamp = props => {
  const getTimestampSequence = (distance, ellapsedDescriptor) => {
    const {
      timeDescriptors,
      timestampSequence,
      wordSpace
    } = props;
    let userTimestamp;

    switch (timestampSequence) {
      case "acb":
        userTimestamp = wordSpace ? `${distance} ${timeDescriptors.ago} ${ellapsedDescriptor}` : `${distance}${timeDescriptors.ago}${ellapsedDescriptor}`;
        break;

      case "bac":
        userTimestamp = wordSpace ? `${ellapsedDescriptor} ${distance} ${timeDescriptors.ago}` : `${ellapsedDescriptor}${distance}${timeDescriptors.ago}`;
        break;

      case "bca":
        userTimestamp = wordSpace ? `${ellapsedDescriptor} ${timeDescriptors.ago} ${distance}` : `${ellapsedDescriptor}${timeDescriptors.ago}${distance}`;
        break;

      case "cab":
        userTimestamp = wordSpace ? `${timeDescriptors.ago} ${distance} ${ellapsedDescriptor}` : `${timeDescriptors.ago}${distance}${ellapsedDescriptor}`;
        break;

      case "cba":
        userTimestamp = wordSpace ? `${timeDescriptors.ago} ${ellapsedDescriptor} ${distance}` : `${timeDescriptors.ago}${ellapsedDescriptor}${distance}`;
        break;

      default:
        userTimestamp = wordSpace ? `${distance} ${ellapsedDescriptor} ${timeDescriptors.ago}` : `${distance}${ellapsedDescriptor}${timeDescriptors.ago}`;
    }

    return userTimestamp;
  };

  const humanizeTimestamp = () => {
    const {
      plural,
      relativeTimeFormatOptions,
      timeDescriptors,
      timestamp
    } = props;
    const asSeconds = Date.parse(timestamp) / 1000; // TODO: handle future timestamps, or bad input?

    const nowAsSeconds = new Date().valueOf() / 1000;
    const rtf = new Intl.RelativeTimeFormat(relativeTimeFormatOptions === null || relativeTimeFormatOptions === void 0 ? void 0 : relativeTimeFormatOptions.locales, relativeTimeFormatOptions === null || relativeTimeFormatOptions === void 0 ? void 0 : relativeTimeFormatOptions.options);
    const timeDifference = nowAsSeconds - asSeconds;
    let distance;
    let ellapsedDescriptor;

    if (timeDifference < 60) {
      distance = Math.round(timeDifference);
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.second, distance) : timeDescriptors.second;
    } else if (timeDifference < 60 * 60) {
      // 1 hour
      distance = Math.round(timeDifference / 60);
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.minute, distance) : timeDescriptors.minute;
    } else if (timeDifference < 60 * 60 * 24) {
      // 1 day
      distance = Math.round(timeDifference / (60 * 60));
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.hour, distance) : timeDescriptors.hour;
    } else if (timeDifference < 60 * 60 * 24 * 7) {
      // 1 week
      distance = Math.round(timeDifference / (60 * 60 * 24));
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.day, distance) : timeDescriptors.day;
    } else if (timeDifference < 60 * 60 * 24 * (365 / 12)) {
      // 1 month
      distance = Math.round(timeDifference / (60 * 60 * 24 * 7));
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.week, distance) : timeDescriptors.week;
    } else if (timeDifference < 60 * 60 * 24 * 30 * 12) {
      // # 1 year
      distance = Math.round(timeDifference / (60 * 60 * 24 * (365 / 12)));
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.month, distance) : timeDescriptors.month;
    } else {
      // More than 1 year
      distance = Math.round(timeDifference / (60 * 60 * 24 * 365));
      ellapsedDescriptor = plural ? pluralize(timeDescriptors.year, distance) : timeDescriptors.year;
    }

    return relativeTimeFormatOptions ? rtf.format(-distance, ellapsedDescriptor) : getTimestampSequence(distance, ellapsedDescriptor);
  };

  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref => {
    let {
      resolvedRoles,
      metadata
    } = _ref;

    const {
      stylesheet: customStylesheet
    } = props,
          otherProps = _objectWithoutProperties(props, _excluded);

    const {
      className
    } = otherProps;
    const styles = stylesheet({
      stylesheet: customStylesheet
    }, resolvedRoles, metadata.colorSchemeId);
    return /*#__PURE__*/React.createElement("div", {
      className: cx(css(styles.timestamp), className)
    }, humanizeTimestamp());
  });
};

Timestamp.displayName = "Timestamp";
Timestamp.propTypes = {
  /**
   * DEPRECATED - use relativeTimeFormatOptions prop
   * If you want to pluralize the elapsed time unit
   * If "true" then it adds an "s" to the end of the time unit
   */
  plural: PropTypes.bool,

  /**
   * These are the options passed to the
   * Intl.RelativeTimeFormat() constructor
   * If this prop is used then the following props
   * will be ignored: plural, timestampSequence,
   * timeDescriptors, wordspace
   */
  relativeTimeFormatOptions: PropTypes.shape({
    locales: PropTypes.string,
    options: PropTypes.shape({
      localeMatcher: PropTypes.string,
      numberingSystem: PropTypes.string,
      style: PropTypes.string,
      numeric: PropTypes.string
    })
  }),

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,

  /**
   * DEPRECATED - use relativeTimeFormatOptions prop
   * An object that allows for localization of all the time words used
   * By default the object property name is the same as the property value
   * Property names: second, minute, hour, day, week, month, year, ago
   */
  timeDescriptors: PropTypes.shape({
    second: PropTypes.string,
    minute: PropTypes.string,
    hour: PropTypes.string,
    day: PropTypes.string,
    week: PropTypes.string,
    month: PropTypes.string,
    year: PropTypes.string,
    ago: PropTypes.string
  }),

  /**
   * ISO date string
   */
  timestamp: PropTypes.string,

  /**
   * DEPRECATED - use relativeTimeFormatOptions prop
   * The sequence in which the timestamp is displayed
   * ie. 4 seconds ago (abc)
   */
  timestampSequence: PropTypes.oneOf(availableSequences),

  /**
   * DEPRECATED - use relativeTimeFormatOptions prop
   * If you want a space between words
   */
  wordSpace: PropTypes.bool
};
Timestamp.defaultProps = {
  plural: true,
  timeDescriptors: {
    second: "second",
    minute: "minute",
    hour: "hour",
    day: "day",
    week: "week",
    month: "month",
    year: "year",
    ago: "ago"
  },
  timestampSequence: availableSequences.ABC,
  wordSpace: true
};

export { availableSequences, Timestamp as default, sequences };
